import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Zap, Flame, Heart, Activity, TrendingUp } from 'lucide-react';
import { WhoopDayData } from '@/hooks/useWhoopData';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { format } from 'date-fns';
import WhoopMetricCard from './WhoopMetricCard';
import { TimeRange } from './WhoopTimeRangeSelector';

interface WhoopStrainDetailProps {
  data: WhoopDayData[];
  selectedData: WhoopDayData | null;
  timeRange: TimeRange;
}

export default function WhoopStrainDetail({ data, selectedData, timeRange }: WhoopStrainDetailProps) {
  const getStrainColor = (score?: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 18) return 'text-accent';
    if (score >= 14) return 'text-primary';
    if (score >= 10) return 'text-secondary';
    return 'text-muted-foreground';
  };

  const getStrainStatus = (score?: number | null) => {
    if (!score) return 'No Data';
    if (score >= 18) return 'All Out';
    if (score >= 14) return 'Strenuous';
    if (score >= 10) return 'Moderate';
    return 'Light';
  };

  const getStrainBgClass = (score?: number | null) => {
    if (!score) return 'bg-muted/20';
    if (score >= 18) return 'bg-accent/10 border-accent/30';
    if (score >= 14) return 'bg-primary/10 border-primary/30';
    if (score >= 10) return 'bg-secondary/10 border-secondary/30';
    return 'bg-muted/10';
  };

  // Calculate stats
  const validStrain = data.filter(d => d.strain_score !== null);
  const avgStrain = validStrain.length > 0 
    ? validStrain.reduce((sum, d) => sum + (d.strain_score || 0), 0) / validStrain.length 
    : 0;
  
  const validCalories = data.filter(d => d.calories_burned !== null);
  const avgCalories = validCalories.length > 0 
    ? validCalories.reduce((sum, d) => sum + (d.calories_burned || 0), 0) / validCalories.length 
    : 0;
  const totalCalories = validCalories.reduce((sum, d) => sum + (d.calories_burned || 0), 0);

  // Calculate trend
  const midPoint = Math.floor(validStrain.length / 2);
  const recentAvg = midPoint > 0 
    ? validStrain.slice(0, midPoint).reduce((sum, d) => sum + (d.strain_score || 0), 0) / midPoint 
    : avgStrain;
  const olderAvg = midPoint > 0 
    ? validStrain.slice(midPoint).reduce((sum, d) => sum + (d.strain_score || 0), 0) / (validStrain.length - midPoint) 
    : avgStrain;
  const trendDiff = recentAvg - olderAvg;
  const trend: 'up' | 'down' | 'stable' = trendDiff > 1 ? 'up' : trendDiff < -1 ? 'down' : 'stable';

  // Prepare chart data
  const chartData = [...data].reverse().map(d => ({
    date: format(new Date(d.date), timeRange === 'month' ? 'MMM d' : 'EEE'),
    fullDate: format(new Date(d.date), 'EEE, MMM d'),
    strain: d.strain_score,
    calories: d.calories_burned
  }));

  // Strain distribution
  const distributionData = [
    { range: 'Light (0-9)', count: validStrain.filter(d => (d.strain_score || 0) < 10).length, fill: 'hsl(var(--muted-foreground))' },
    { range: 'Moderate (10-13)', count: validStrain.filter(d => (d.strain_score || 0) >= 10 && (d.strain_score || 0) < 14).length, fill: 'hsl(var(--secondary))' },
    { range: 'Strenuous (14-17)', count: validStrain.filter(d => (d.strain_score || 0) >= 14 && (d.strain_score || 0) < 18).length, fill: 'hsl(var(--primary))' },
    { range: 'All Out (18+)', count: validStrain.filter(d => (d.strain_score || 0) >= 18).length, fill: 'hsl(var(--accent))' },
  ];

  // Find highest strain day
  const maxStrainDay = validStrain.reduce((max, d) => 
    (d.strain_score || 0) > (max?.strain_score || 0) ? d : max, validStrain[0]);

  // For period views, calculate summary values
  const periodLabel = timeRange === 'day' 
    ? (selectedData ? format(new Date(selectedData.date), 'EEEE, MMMM d') : 'Today')
    : timeRange === 'week' 
      ? '7-Day Average' 
      : '30-Day Average';

  const displayStrain = timeRange === 'day' 
    ? selectedData?.strain_score 
    : avgStrain;
  const displayCalories = timeRange === 'day' 
    ? selectedData?.calories_burned 
    : avgCalories;

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Main Summary - Shows single day or averages based on timeRange */}
      {(selectedData || data.length > 0) && (
        <Card className={`border-2 ${getStrainBgClass(displayStrain)}`}>
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              {timeRange === 'day' ? 'Day Strain' : 'Average Strain'}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {periodLabel}
              {timeRange !== 'day' && ` • ${data.length} days of data`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6">
            <div className="text-center py-2 sm:py-4">
              <div className={`text-5xl sm:text-7xl font-bold ${getStrainColor(displayStrain)}`}>
                {displayStrain ? displayStrain.toFixed(1) : '--'}
              </div>
              <p className={`text-base sm:text-lg font-medium mt-1 sm:mt-2 ${getStrainColor(displayStrain)}`}>
                {getStrainStatus(displayStrain)}
              </p>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <WhoopMetricCard
                icon={Flame}
                label={timeRange === 'day' ? 'Calories' : 'Avg Calories'}
                value={displayCalories ? Math.round(displayCalories).toLocaleString() : '--'}
                colorClass="text-primary"
                bgClass="bg-primary/5"
              />
              <WhoopMetricCard
                icon={TrendingUp}
                label={timeRange === 'day' ? 'Total Calories' : 'Total Burned'}
                value={Math.round(totalCalories).toLocaleString()}
                colorClass="text-secondary"
                bgClass="bg-secondary/5"
              />
              <WhoopMetricCard
                icon={Activity}
                label="Max Strain"
                value={maxStrainDay?.strain_score?.toFixed(1) || '--'}
                colorClass="text-accent"
                bgClass="bg-accent/5"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Overview Cards - Only show for week/month with multiple days */}
      {timeRange !== 'day' && data.length > 1 && (
        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          <WhoopMetricCard
            icon={Zap}
            label={`Avg Strain (${timeRange === 'week' ? '7d' : '30d'})`}
            value={avgStrain.toFixed(1)}
            trend={trend}
            trendValue={`${trendDiff > 0 ? '+' : ''}${trendDiff.toFixed(1)}`}
            colorClass={getStrainColor(avgStrain)}
            bgClass={getStrainBgClass(avgStrain)}
          />
          <WhoopMetricCard
            icon={Flame}
            label="Avg Calories"
            value={Math.round(avgCalories).toLocaleString()}
            colorClass="text-primary"
            bgClass="bg-primary/5"
          />
          <WhoopMetricCard
            icon={TrendingUp}
            label="Total Calories"
            value={Math.round(totalCalories).toLocaleString()}
            colorClass="text-secondary"
            bgClass="bg-secondary/5"
          />
          <WhoopMetricCard
            icon={Activity}
            label="Days Tracked"
            value={validStrain.length}
            colorClass="text-accent"
            bgClass="bg-accent/5"
          />
        </div>
      )}

      {/* Strain Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Strain Trend</CardTitle>
          <CardDescription>Track your daily cardiovascular load over time</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="strainGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--secondary))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--secondary))" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 21]} stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [value?.toFixed(1), 'Strain']}
                labelFormatter={(label, payload) => payload[0]?.payload?.fullDate || label}
              />
              <ReferenceLine y={avgStrain} stroke="hsl(var(--primary))" strokeDasharray="5 5" />
              <ReferenceLine y={14} stroke="hsl(var(--accent))" strokeDasharray="3 3" strokeOpacity={0.5} />
              <Area
                type="monotone"
                dataKey="strain"
                stroke="hsl(var(--secondary))"
                strokeWidth={2}
                fill="url(#strainGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Calories Burned Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Calories Burned</CardTitle>
          <CardDescription>Daily energy expenditure</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
                formatter={(value: number) => [`${Math.round(value).toLocaleString()} cal`, 'Calories']}
                labelFormatter={(label, payload) => payload[0]?.payload?.fullDate || label}
              />
              <ReferenceLine y={avgCalories} stroke="hsl(var(--accent))" strokeDasharray="5 5" />
              <Bar 
                dataKey="calories" 
                fill="hsl(var(--primary))"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Strain Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Strain Level Distribution</CardTitle>
          <CardDescription>Breakdown of your daily strain intensities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {distributionData.map((item) => (
              <div key={item.range} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{item.range}</span>
                  <span className="text-muted-foreground">
                    {item.count} days ({validStrain.length > 0 ? Math.round((item.count / validStrain.length) * 100) : 0}%)
                  </span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: validStrain.length > 0 ? `${(item.count / validStrain.length) * 100}%` : '0%',
                      backgroundColor: item.fill
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Strain Insights */}
      <Card className="bg-gradient-to-br from-secondary/5 to-primary/5 border-secondary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-secondary" />
            Strain & Recovery Balance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-foreground/90 leading-relaxed">
            Strain measures the cardiovascular load accumulated through activity. Balancing strain with recovery is key to avoiding burnout and maximizing performance.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-3 bg-muted/20 rounded-lg border border-muted">
              <p className="text-sm font-medium text-muted-foreground">Light (0-9)</p>
              <p className="text-xs text-muted-foreground/80 mt-1">
                Minimal cardiovascular load. Good for recovery days.
              </p>
            </div>
            <div className="p-3 bg-secondary/10 rounded-lg border border-secondary/30">
              <p className="text-sm font-medium text-secondary">Moderate (10-13)</p>
              <p className="text-xs text-muted-foreground mt-1">
                Typical daily activity. Maintains fitness without overload.
              </p>
            </div>
            <div className="p-3 bg-primary/10 rounded-lg border border-primary/30">
              <p className="text-sm font-medium text-primary">Strenuous (14-17)</p>
              <p className="text-xs text-muted-foreground mt-1">
                Significant workout day. Requires good recovery next.
              </p>
            </div>
            <div className="p-3 bg-accent/10 rounded-lg border border-accent/30">
              <p className="text-sm font-medium text-accent">All Out (18+)</p>
              <p className="text-xs text-muted-foreground mt-1">
                Maximum effort. Allow extended recovery time.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
